import React, { useContext, useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import PropTypes from 'prop-types';
import * as R from 'ramda';

import { AuthContext } from 'authentication/AuthContext';
import { DrawerLayoutWrapper } from 'common';
import { convertDataCompanyId } from 'utils/helpers/format';

const initialState = {
  carrierName: '',
  custRestricted: false,
  customerIds: [],
  location: '',
  supplierName: '',
};

const orDashes = R.or(R.__, '--');

const DataDisplayItem = ({ label, value }) => (
  <Stack spacing={0.5} sx={{ mb: 2 }}>
    <Typography variant="caption">{label}</Typography>
    <Typography variant="body1bold">{orDashes(value)}</Typography>
  </Stack>
);

DataDisplayItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

const EditDrawer = ({
  carrierAuthorizationId,
  customers,
  details,
  fetchCustomers,
  fetchCarrierAuthorizationDetails,
  editCarrierAuthorization,
  isEditing,
  isCustomersLoading,
  isDetailsLoading,
  resetCarrierAuthorizationId,
  setIsEditing,
}) => {
  const { currentAffiliate } = useContext(AuthContext);
  const [confirmDialogOpened, setConfirmDialogOpened] = useState(false);
  const [drawerState, setDrawerState] = useState(initialState);
  const { carrierName, custRestricted, customerIds, location, supplierName } = drawerState;

  useEffect(() => {
    if (!R.isNil(carrierAuthorizationId)) {
      const params = {
        carrierAuthorizationId,
      };
      fetchCarrierAuthorizationDetails(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierAuthorizationId]);

  useEffect(() => {
    if (!R.isNil(currentAffiliate) && !R.isNil(details)) {
      const params = {
        locationIds: [details.locationId],
        supplier: {
          companyCustomerIdName: currentAffiliate.customerIdName,
          dataCompanyId: currentAffiliate.dataCompanyId,
        },
      };
      fetchCustomers(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAffiliate, details]);

  useEffect(() => {
    if (!R.isNil(details) && !R.isNil(customers)) {
      setDrawerState({
        carrierName: details.carrierName,
        custRestricted: details.custRestricted,
        customerIds: details.customers,
        location: details.location,
        supplierName: details.supplierName,
      });
    }
  }, [customers, details]);

  const closeResetDrawer = () => {
    setIsEditing(false);
    setDrawerState(initialState);
    resetCarrierAuthorizationId();
    setConfirmDialogOpened(false);
  };

  const handleClose = () => {
    const custRestrictedMatch = R.equals(drawerState.custRestricted, details.custRestricted);
    const selectedCustomersMatch = R.equals(drawerState.customerIds, details.customers);

    if (custRestrictedMatch && selectedCustomersMatch) {
      closeResetDrawer();
    } else {
      setConfirmDialogOpened(true);
    }
  };

  // prettier-ignore
  const handleChange = key => R.compose(
    setDrawerState,
    R.assoc(key),
    R.nthArg(1)
  );

  const handleChangeRadio = (_, value) => {
    setDrawerState(
      R.evolve(
        {
          custRestricted: R.always(value === 'true'), // convert string value to boolean
          customerIds: R.always(initialState.customerIds),
        },
        drawerState
      )
    );
  };

  const renderTags = (tags, getTagProps) =>
    tags.map((item, index) => (
      <Chip
        label={item.customerName}
        sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
        variant="outlined"
        {...getTagProps({ index })}
      />
    ));

  // prettier-ignore
  const isInvalid = [
    custRestricted && R.isEmpty(customerIds)
  ].includes(true);

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpened(false);
  };

  const handleSave = () => {
    editCarrierAuthorization({
      carrierAuthorizationId,
      customerIds: R.pluck('customerId', customerIds),
    });
    closeResetDrawer();
  };

  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={isEditing}
      slotProps={{
        paper: { sx: { width: { lg: 380, xs: '100%' } } },
      }}
      variant="temporary"
    >
      <DrawerLayoutWrapper
        buttons={
          <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
            <Button onClick={handleClose} sx={{ width: 160 }} variant="outlined">
              Cancel
            </Button>
            <Button
              disabled={
                isDetailsLoading ||
                (R.equals(drawerState.custRestricted, details?.custRestricted) &&
                  R.equals(drawerState.customerIds, details?.customers)) ||
                isInvalid
              }
              onClick={handleSave}
              sx={{ width: 160 }}
            >
              Save
            </Button>
          </Box>
        }
        isLoading={isDetailsLoading}
        onHeaderButtonClick={handleClose}
        title="Edit Carrier Authorization"
      >
        <Box sx={{ pt: 3, px: { lg: 3, xs: 2 } }}>
          <Box>
            <Box
              spacing={0.5}
              sx={{ alignItems: 'end', display: 'grid', gridTemplateColumns: '1fr 1fr', mb: 2 }}
            >
              <Typography sx={{ gridColumn: '1 / 3' }} variant="caption">
                Supplier
              </Typography>
              <Typography variant="body1bold">{orDashes(supplierName)}</Typography>
              <Typography sx={{ justifySelf: 'end' }} variant="caption">
                {orDashes(convertDataCompanyId(currentAffiliate?.dataCompanyId))}
              </Typography>
            </Box>
            <DataDisplayItem label="Carrier" value={carrierName} />
            <DataDisplayItem label="Location" value={location} />
          </Box>
          <Divider sx={{ mb: 2 }} />
          <FormControl>
            <FormLabel sx={{ lineHeight: 1.5, mb: 2 }}>
              Would you like to restrict Customers? The Carrier restriction function indicates if a
              carrier is restricted to certain customers within a Customer Authorization.
            </FormLabel>
            <RadioGroup onChange={handleChangeRadio} sx={{ mb: 2 }} value={custRestricted}>
              <FormControlLabel
                control={<Radio />}
                label={
                  <>
                    Allow loading for <strong>all</strong> customers
                  </>
                }
                value={false}
              />
              <FormControlLabel
                control={<Radio />}
                label={
                  <>
                    Allow loading for <strong>selected</strong> customers
                  </>
                }
                // eslint-disable-next-line react/jsx-boolean-value
                value={true}
              />
            </RadioGroup>
          </FormControl>
          {custRestricted && (
            <Autocomplete
              getOptionLabel={R.prop('customerName')}
              isOptionEqualToValue={R.eqProps('customerId')}
              loading={isCustomersLoading}
              multiple
              onChange={handleChange('customerIds')}
              options={customers || []}
              renderInput={inputParams => <TextField {...inputParams} label="Search Customers" />}
              renderTags={renderTags}
              sx={{ '& .MuiAutocomplete-endAdornment': { top: 10 } }}
              value={customerIds}
            />
          )}
        </Box>
        {confirmDialogOpened && (
          <Dialog
            maxWidth="xs"
            onClose={closeResetDrawer}
            open
            slotProps={{
              paper: { elevation: 10 },
            }}
            sx={{ '& .MuiDialog-paper': { height: 180, width: 330 } }}
          >
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogContent>
              Are you sure you want to leave this page? You have unsaved changes.
            </DialogContent>
            <DialogActions sx={{ pb: 2, px: 2 }}>
              <Button autoFocus onClick={closeResetDrawer} variant="outlined">
                Continue
              </Button>
              <Button onClick={handleCloseConfirmDialog}>Keep Editing</Button>
            </DialogActions>
          </Dialog>
        )}
      </DrawerLayoutWrapper>
    </Drawer>
  );
};

EditDrawer.propTypes = {
  carrierAuthorizationId: PropTypes.number,
  customers: PropTypes.arrayOf(PropTypes.shape({})),
  details: PropTypes.shape({
    carrierName: PropTypes.string,
    custRestricted: PropTypes.bool,
    customers: PropTypes.arrayOf(PropTypes.shape({})),
    location: PropTypes.string,
    locationId: PropTypes.number,
    supplierName: PropTypes.string,
  }),
  editCarrierAuthorization: PropTypes.func.isRequired,
  fetchCarrierAuthorizationDetails: PropTypes.func.isRequired,
  fetchCustomers: PropTypes.func.isRequired,
  isCustomersLoading: PropTypes.bool.isRequired,
  isDetailsLoading: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool.isRequired,
  resetCarrierAuthorizationId: PropTypes.func.isRequired,
  setIsEditing: PropTypes.func.isRequired,
};

EditDrawer.defaultProps = {
  carrierAuthorizationId: undefined,
  customers: undefined,
  details: undefined,
};

export default EditDrawer;
